import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabStatsLine } from './TabStatsLine';
import { Users, Search, Sparkles, MessageSquare, UserPlus, Loader2, UserCheck } from 'lucide-react';
import { useMentorship } from '@/hooks/useMentorship';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { getTierFromProfile } from '@/lib/tier-utils';
import { BETA_CONFIG } from '@/config/beta';
import { MentorCard } from '@/components/mentorship/MentorCard';
import { ConnectionCard } from '@/components/mentorship/ConnectionCard';
import { MessageThread } from '@/components/mentorship/MessageThread';
import { MentorProfileForm } from '@/components/mentorship/MentorProfileForm';
import type { MentorshipConnection } from '@/types/mentorship';

export const MentorshipTab = () => {
  const { user } = useAuth();
  const {
    myProfile,
    myOrganization,
    mentors,
    connections,
    loading,
    matchesLoading,
    saveMyProfile,
    requestMentorship,
    respondToRequest,
    endConnection,
    getMatches,
  } = useMentorship();

  const { profile } = useProfile();
  const userTier = getTierFromProfile(profile);
  const isPro = BETA_CONFIG.allFeaturesUnlocked || userTier === 'pro' || userTier === 'beta_pro';

  const [activeTab, setActiveTab] = useState('profile');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeConversation, setActiveConversation] = useState<MentorshipConnection | null>(null);

  const matches = getMatches();
  const pendingRequests = connections.filter(
    c => c.status === 'pending' && c.mentor_id === user?.id
  );
  const activeConnections = connections.filter(c => c.status === 'accepted');
  const myRequests = connections.filter(
    c => c.status === 'pending' && c.mentee_id === user?.id
  );

  // Filter mentors by search
  const filteredMentors = mentors.filter(mentor => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      mentor.display_name?.toLowerCase().includes(query) ||
      mentor.first_name?.toLowerCase().includes(query) ||
      mentor.last_name?.toLowerCase().includes(query) ||
      mentor.skills_can_teach.some(s => s.toLowerCase().includes(query)) ||
      mentor.job_role?.toLowerCase().includes(query)
    );
  });

  // Check if user has existing connection with a mentor
  const hasConnectionWith = (mentorId: string) => {
    return connections.some(
      c => c.mentor_id === mentorId && c.mentee_id === user?.id && 
           (c.status === 'pending' || c.status === 'accepted')
    );
  };

  // Calculate mentorship stats - MUST be before any early returns
  const mentorStats = useMemo(() => {
    // Profile completeness (rough estimate based on filled fields)
    const profileFields = [
      myProfile?.bio,
      myProfile?.skills_can_teach?.length > 0,
      myProfile?.skills_want_to_learn?.length > 0,
      myProfile?.years_experience,
      myProfile?.preferred_meeting_frequency,
    ];
    const filledFields = profileFields.filter(Boolean).length;
    const profileComplete = Math.round((filledFields / profileFields.length) * 100);
    
    return {
      activeMatches: activeConnections.length,
      profileComplete,
      pendingCount: pendingRequests.length + myRequests.length,
    };
  }, [myProfile, activeConnections, pendingRequests, myRequests]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show message thread if active
  if (activeConversation) {
    return (
      <Card className="card-elevated">
        <CardContent className="p-0">
          <MessageThread
            connection={activeConversation}
            onBack={() => setActiveConversation(null)}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with pending badge */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Mentorship Hub
          </h2>
          <p className="text-sm text-muted-foreground">
            Connect with mentors and accelerate your growth
          </p>
          <TabStatsLine 
            stats={[
              { value: mentorStats.activeMatches, label: 'active matches', icon: <UserCheck className="w-3.5 h-3.5" /> },
              { value: `${mentorStats.profileComplete}%`, label: 'profile complete' },
              { value: matches.length, label: 'potential mentors', icon: <Sparkles className="w-3.5 h-3.5" /> },
            ]}
          />
        </div>
        {pendingRequests.length > 0 && (
          <Badge variant="default" className="animate-pulse self-start">
            {pendingRequests.length} pending
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile" className="gap-1 sm:gap-2">
            <UserPlus className="hidden sm:block w-4 h-4 shrink-0" />
            <span className="text-xs sm:text-sm">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="discover" className="gap-1 sm:gap-2">
            <Search className="hidden sm:block w-4 h-4 shrink-0" />
            <span className="text-xs sm:text-sm">Discover</span>
          </TabsTrigger>
          <TabsTrigger value="matches" className="gap-1 sm:gap-2">
            <Sparkles className="hidden sm:block w-4 h-4 shrink-0" />
            <span className="text-xs sm:text-sm">Matches</span>
            {matches.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {matches.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="connections" className="gap-1 sm:gap-2">
            <Users className="hidden sm:block w-4 h-4 shrink-0" />
            <span className="text-xs sm:text-sm">Connect</span>
            {(pendingRequests.length + activeConnections.length) > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {pendingRequests.length + activeConnections.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Discover Tab */}
        <TabsContent value="discover" className="space-y-4 mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, skill, or role..."
              className="pl-10"
            />
          </div>

          {filteredMentors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No mentors found</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {searchQuery
                  ? 'Try adjusting your search terms.'
                  : 'Be the first to offer mentorship! Update your profile in the "My Profile" tab.'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredMentors.map((mentor) => (
                <MentorCard
                  key={mentor.id}
                  mentor={mentor}
                  onRequestMentorship={requestMentorship}
                  hasExistingConnection={hasConnectionWith(mentor.user_id)}
                  isPro={isPro}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches" className="space-y-4 mt-4">
          {!myProfile?.is_seeking_mentor || myProfile.skills_want_to_learn.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Set up your profile to see matches</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                Enable "Seek Mentorship" and add skills you want to learn to get matched with mentors.
              </p>
              <Button onClick={() => setActiveTab('profile')}>
                Set Up Profile
              </Button>
            </div>
          ) : matchesLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
              <h3 className="text-lg font-medium mb-2">Finding your best matches...</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                AI is analyzing skill compatibility to find mentors who can help you grow
              </p>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No matches yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                We couldn't find mentors matching your skills. Check back later or browse all mentors.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>AI-powered matches based on skill compatibility</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {matches.map((match) => (
                  <MentorCard
                    key={match.profile.id}
                    mentor={match.profile}
                    matchingSkills={match.matchingSkills}
                    matchScore={match.matchScore}
                    onRequestMentorship={requestMentorship}
                    hasExistingConnection={hasConnectionWith(match.profile.user_id)}
                    isPro={isPro}
                  />
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-6 mt-4">
          {/* Pending requests (as mentor) */}
          {pendingRequests.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Pending Requests ({pendingRequests.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {pendingRequests.map((connection) => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    onAccept={(id) => respondToRequest(id, true)}
                    onDecline={(id) => respondToRequest(id, false)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* My pending requests (as mentee) */}
          {myRequests.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Your Pending Requests ({myRequests.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {myRequests.map((connection) => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Active connections */}
          {activeConnections.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Active Connections ({activeConnections.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {activeConnections.map((connection) => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    onMessage={(id) => {
                      const conn = connections.find(c => c.id === id);
                      if (conn) setActiveConversation(conn);
                    }}
                    onEnd={(id) => endConnection(id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {pendingRequests.length === 0 && myRequests.length === 0 && activeConnections.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No connections yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                Start by discovering mentors and sending connection requests.
              </p>
              <Button onClick={() => setActiveTab('discover')}>
                Find Mentors
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4">
          <MentorProfileForm 
            profile={myProfile} 
            organization={myOrganization || undefined}
            onSave={saveMyProfile} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
